import { Injectable, NgZone } from '@angular/core';
import { DetectDevicesService } from '@fi-shared/fi-shared-services/detect-devices.service';
import { isDefined } from '@fi-shared/utils';
export interface IScrollToElementWithCompletionArgs {
    targetElement: HTMLElement;
    scrollableElement: HTMLElement;
    maxScrollDurationMS: number;
    targetElementOffset?: number;
    options?: ScrollToOptions;
}

type TScrollToEasing = 'easeInOutCubic';

interface IScrollToArgs {
    speed?: number;
    easing?: TScrollToEasing;
}

@Injectable()
export class FiScrollToElementAnimateService {
    constructor(
        private ngZone: NgZone,
        private detectDevice: DetectDevicesService,
    ) {}

    selfAnimate(callback) {
        this.ngZone.runOutsideAngular(() => {
            window.setTimeout(callback, 10);
        });
    }

    getAnimateFunction(): (callback: Function) => any {
        // requestAnimationFrame on mobile devices is lagging, use animation with timeout instead
        return this.detectDevice.isMobileSmall()
            ? (callback) => this.selfAnimate(callback)
            : window.requestAnimationFrame;
    }

    scrollToY(scrollElement: HTMLElement, scrollTargetY: number, args?: IScrollToArgs) {
        if (!isDefined(args)) {
            args = {};
        }
        if (!isDefined(args.speed)) {
            args.speed = 1000;
        }
        if (!isDefined(args.easing)) {
            args.easing = 'easeInOutCubic';
        }
        const animateFunc = this.getAnimateFunction();

        let scrollY = scrollElement.scrollTop,
            currentTime = 0;

        // min time .1, max time 2 seconds
        const time = Math.max(0.1, Math.min(Math.abs(scrollY - scrollTargetY) / args.speed, 2));
        // add animation loop
        function tick() {
            currentTime += 1 / 60;

            const p = currentTime / time;
            const t = easingEquations[args.easing](p);

            if (p < 1) {
                animateFunc(tick);

                scrollElement.scrollTop = scrollY + (scrollTargetY - scrollY) * t;
            } else {
                scrollElement.scrollTop = scrollTargetY;
            }
        }

        // call it once to get started
        tick();
    }

    scrollToElementBottom(element: HTMLElement, scrollableElement: HTMLElement, addTopOffset = 0) {
        this.scrollToY(
            scrollableElement,
            element.offsetTop +
                element.getBoundingClientRect().height -
                scrollableElement.getBoundingClientRect().height +
                addTopOffset,
        );
    }

    scrollTo(element: HTMLElement, scrollableElement: HTMLElement, args?: IScrollToArgs) {
        this.scrollToY(scrollableElement, element.offsetTop, args);
    }

    scrollToElementIntoView(element: HTMLElement, scrollableElement: HTMLElement, args?: IScrollToArgs) {
        if (element.offsetTop < scrollableElement.scrollTop) {
            this.scrollToY(scrollableElement, element.offsetTop, args);
        } else {
            const targetBottom = element.offsetTop + element.getBoundingClientRect().height,
                currentBottom = scrollableElement.scrollTop + scrollableElement.getBoundingClientRect().height;
            if (targetBottom > currentBottom) {
                this.scrollToY(
                    scrollableElement,
                    targetBottom - scrollableElement.getBoundingClientRect().height,
                    args,
                );
            }
        }
    }

    scrollToElementWithCompletion(args: IScrollToElementWithCompletionArgs): Promise<boolean> {
        return new Promise((resolve) => {
            const options: ScrollToOptions = args.options || { behavior: 'smooth' };

            const targetElRect: DOMRect = args.targetElement.getBoundingClientRect();
            const containerRect: DOMRect = args.scrollableElement.getBoundingClientRect();
            if (targetElRect.top >= containerRect.top && targetElRect.bottom <= containerRect.bottom) {
                resolve(true);
                return;
            }

            const handleScrollEnd = () => {
                args.scrollableElement.removeEventListener('scroll', scrollListener);
                resolve(true);
            };

            let scrollTimeout: NodeJS.Timeout;

            let scrolling: boolean = false;
            const scrollListener = () => {
                scrolling = true;
                if (isDefined(scrollTimeout)) {
                    clearTimeout(scrollTimeout);
                }
                scrollTimeout = setTimeout(() => {
                    if (scrolling) {
                        scrolling = false;
                        handleScrollEnd();
                    }
                }, 150);
            };

            args.scrollableElement.addEventListener('scroll', scrollListener);
            const elementOffsetTop: number = args.targetElementOffset
                ? args.targetElement.offsetTop + args.targetElementOffset
                : args.targetElement.offsetTop;
            args.scrollableElement.scrollTo({
                ...options,
                top: elementOffsetTop - args.scrollableElement.offsetTop,
            });

            setTimeout(() => {
                if (scrollTimeout) {
                    clearTimeout(scrollTimeout);
                    handleScrollEnd();
                }
            }, args.maxScrollDurationMS);
        });
    }
}

// easing equations from https://github.com/danro/easing-js/blob/master/easing.js
const easingEquations = {
    easeInOutCubic: function (pos) {
        if ((pos /= 0.5) < 1) return 0.5 * Math.pow(pos, 3);
        return 0.5 * (Math.pow(pos - 2, 3) + 2);
    },
};
